import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, Send, MoreVertical, Image as ImageIcon, Smile, Phone, Video, Info, Users, ArrowLeft, Plus, ArrowUp, CreditCard } from 'lucide-react';
import { UserMenu } from '../components/UserMenu';
import { Logo } from '../components/Logo';
import { NotificationBell } from '../components/NotificationBell';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { collection, query, orderBy, onSnapshot, addDoc, serverTimestamp, doc, setDoc, deleteDoc, where, getDoc, getDocs } from 'firebase/firestore';
import { createNotification } from '../components/NotificationBell';
import { useStorage } from '../hooks/useStorage';
import toast from 'react-hot-toast';

export default function MessagesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('all');
  const [conversations, setConversations] = useState<any[]>([]);
  const [selectedConv, setSelectedConv] = useState<any>(null);
  const [messageInput, setMessageInput] = useState('');
  const [messages, setMessages] = useState<any[]>([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedForEdit, setSelectedForEdit] = useState<Set<string>>(new Set());

  const [showConvMenu, setShowConvMenu] = useState(false);

  const targetConvId = location.state?.conversationId;

  useEffect(() => {
    if (!user) return;

    // Filter by participants (security)
    const q = query(
      collection(db, 'conversations'), 
      where('participants', 'array-contains', user.uid),
      orderBy('updatedAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const convs = snapshot.docs.map(doc => {
        const data = doc.data();
        let name = data.name;
        let avatar = data.avatar;

        // For direct messages, use the OTHER participant's details if available
        if (!data.isGroup && data.participantDetails) {
          const otherId = data.participants.find((p: string) => p !== user.uid);
          if (otherId && data.participantDetails[otherId]) {
            name = data.participantDetails[otherId].name;
            avatar = data.participantDetails[otherId].avatar;
          }
        }

        // Use per-user unread count
        const userUnread = data.unreadCounts?.[user.uid] || 0;

        return { id: doc.id, ...data, displayName: name, displayAvatar: avatar, unreadCount: userUnread };
      });
      setConversations(convs);

      // Handle auto-selection from navigation state
      if (targetConvId) {
        const target = convs.find(c => c.id === targetConvId);
        if (target) setSelectedConv(target);
      }
    }, (error) => {
      console.error("Error fetching conversations:", error);
    });

    return () => unsubscribe();
  }, [user, targetConvId]);

  useEffect(() => {
    // Only auto-select on desktop (md breakpoint is 768px)
    if (conversations.length > 0 && !selectedConv && !targetConvId && window.innerWidth >= 768) {
      setSelectedConv(conversations[0]);
    }
  }, [conversations, selectedConv, targetConvId]);

  useEffect(() => {
    if (!selectedConv) return;
    const q = query(collection(db, 'conversations', selectedConv.id, 'messages'), orderBy('createdAt', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs = snapshot.docs.map(doc => {
        const data = doc.data();
        let timeStr = "";
        if (data.createdAt) {
           const d = data.createdAt.toDate ? data.createdAt.toDate() : new Date();
           timeStr = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        }
        return {
          id: doc.id,
          ...data,
          isMe: data.senderId === user?.uid,
          time: timeStr
        };
      });
      setMessages(msgs);

      // Mark messages as read when they arrive if this conversation is selected
      if (selectedConv && user) {
        const docRef = doc(db, 'conversations', selectedConv.id);
        setDoc(docRef, {
          unreadCounts: {
            [user.uid]: 0
          }
        }, { merge: true }).catch(err => console.error("Error marking as read:", err));
      }
    }, (error) => {
      console.error("Error fetching messages:", error);
    });
    return () => unsubscribe();
  }, [selectedConv, user]);

  const [isBlocked, setIsBlocked] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    const fetchUserRole = async () => {
      const userSnap = await getDoc(doc(db, 'users', user.uid));
      if (userSnap.exists()) {
        setUserRole(userSnap.data().role);
      }
    };
    fetchUserRole();
  }, [user]);

  useEffect(() => {
    if (!selectedConv || !user || userRole !== 'stand') {
      setIsBlocked(false);
      return;
    }

    const checkStatus = async () => {
      // Find the other participant (the festival organizer)
      const otherId = selectedConv.participants.find((p: string) => p !== user.uid);
      if (!otherId) return;

      // Look for an application from THIS stand to THIS festival/organizer
      // Note: We don't have festivalId directly in conversation, but we can verify 
      // if any application by this user for the otherId's festivals is confirmed.
      try {
        const q = query(collection(db, 'applications'), where('applicantId', '==', user.uid));
        const snap = await getDocs(q);
        const apps = snap.docs.map(d => d.data());
        
        // If there's an application for a festival owned by otherId
        // For simplicity: if the user (stand) has NO confirmed application at all, 
        // we might block them or if they have one for this specific encounter.
        // Let's check if the current conversation involves a "festival" role.
        if (selectedConv.role === 'festival') {
           const confirmedApp = apps.find(a => a.status === 'confirme');
           if (!confirmedApp) {
             setIsBlocked(true);
             return;
           }
        }
        setIsBlocked(false);
      } catch (err) {
        console.error("Error checking block status:", err);
      }
    };
    
    checkStatus();
  }, [selectedConv, user, userRole]);

  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const { uploadFile, isUploading } = useStorage();

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user || !selectedConv || isBlocked) return;
    
    try {
      const storagePath = `conversations/${selectedConv.id}/${Date.now()}_${file.name}`;
      const toastId = toast.loading("Envoi de l'image...");
      const url = await uploadFile(file, storagePath);
      toast.dismiss(toastId);
      
      const msgData = {
        sender: user.displayName || 'Utilisateur',
        senderId: user.uid,
        text: '📷 Photo',
        imageUrl: url,
        createdAt: serverTimestamp(),
        avatar: user.photoURL || "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=150&q=80"
      };
      
      await addDoc(collection(db, 'conversations', selectedConv.id, 'messages'), msgData);

      const unreadCountsUpdate: Record<string, any> = {};
      selectedConv.participants.forEach((pId: string) => {
        if (pId !== user.uid) {
          const currentCount = selectedConv.unreadCounts?.[pId] || 0;
          unreadCountsUpdate[pId] = currentCount + 1;

          createNotification({
            userId: pId,
            title: `Message de ${user.displayName || 'Utilisateur'}`,
            message: '📷 Photo',
            type: 'info',
            link: `/messages`
          });
        } else {
          unreadCountsUpdate[pId] = 0;
        }
      });

      await setDoc(doc(db, 'conversations', selectedConv.id), {
        lastMessage: `${user.displayName || 'Moi'}: 📷 Photo`,
        updatedAt: serverTimestamp(),
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        unreadCounts: unreadCountsUpdate
      }, { merge: true });
    } catch (err) {
      toast.error("Erreur lors de l'envoi de l'image");
      console.error(err);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!messageInput.trim() || !selectedConv || !user || isBlocked) return;
    
    const msgText = messageInput;
    setMessageInput('');
    
    try {
      const msgData = {
        sender: user.displayName || 'Utilisateur',
        senderId: user.uid,
        text: msgText,
        createdAt: serverTimestamp(),
        avatar: user.photoURL || "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=150&q=80"
      };
      
      await addDoc(collection(db, 'conversations', selectedConv.id, 'messages'), msgData);

      // Update conversation metadata and increment unread count for other participants
      const unreadCountsUpdate: Record<string, any> = {};
      selectedConv.participants.forEach((pId: string) => {
        if (pId !== user.uid) {
          // In a real app we'd use fieldValue.increment(1) but for simplicity:
          const currentCount = selectedConv.unreadCounts?.[pId] || 0;
          unreadCountsUpdate[pId] = currentCount + 1;

          // Create standard notification for the recipient
          createNotification({
            userId: pId,
            title: `Message de ${user.displayName || 'Utilisateur'}`,
            message: msgText,
            type: 'info',
            link: `/messages`
          });
        } else {
          unreadCountsUpdate[pId] = 0;
        }
      });

      await setDoc(doc(db, 'conversations', selectedConv.id), {
        lastMessage: `${user.displayName || 'Moi'}: ${msgText}`,
        updatedAt: serverTimestamp(),
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        unreadCounts: unreadCountsUpdate
      }, { merge: true });
    } catch (err) {
      console.error("Error sending message:", err);
    }
  };

  return (
    <div className="bg-black text-white flex flex-col font-sans selection:bg-[#FF5C00]/30 fixed inset-0 top-[64px] bottom-[70px] md:relative md:inset-auto md:top-auto md:bottom-auto md:h-[calc(100dvh-64px)] overflow-hidden z-[40]">
      {/* Main Content */}
      <main className="flex-1 max-w-[1600px] w-full mx-auto md:p-6 flex flex-col md:flex-row md:gap-6 overflow-hidden">
        
          {/* Left Sidebar - Conversations List */}
        <aside className={`w-full md:w-80 lg:w-96 flex-col bg-black border-r border-white/5 overflow-hidden shrink-0 h-full ${selectedConv ? 'hidden md:flex' : 'flex'}`}>
          <div className="p-5">
            <div className="flex justify-between items-center mb-4">
              <h1 className="text-[28px] font-bold text-white tracking-tight">Messages</h1>
              <button 
                onClick={() => {
                  setIsEditMode(!isEditMode);
                  setSelectedForEdit(new Set());
                }}
                className="text-[#007AFF] text-[17px] font-medium hover:opacity-70 transition-opacity"
              >
                {isEditMode ? 'OK' : 'Modifier'}
              </button>
            </div>
            
            <div className="relative mb-2">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-gray-500" />
              </div>
              <input 
                type="text" 
                placeholder="Rechercher" 
                className="block w-full bg-[#1C1C1E] border-none rounded-xl py-2 pl-10 pr-3 text-[17px] text-white placeholder-gray-500 focus:outline-none focus:ring-0 transition-all"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto no-scrollbar">
            {conversations
              .filter(c => {
                const matchesSearch = c.displayName?.toLowerCase().includes('') ?? true; // Add search filter later if needed
                if (activeTab === 'all') return !c.isArchived;
                if (activeTab === 'unread') return !c.isArchived && c.unreadCount > 0;
                if (activeTab === 'archived') return c.isArchived;
                return true;
              })
              .map(conv => (
              <button 
                key={conv.id}
                onClick={() => {
                  if (isEditMode) {
                    const newSelected = new Set(selectedForEdit);
                    if (newSelected.has(conv.id)) {
                      newSelected.delete(conv.id);
                    } else {
                      newSelected.add(conv.id);
                    }
                    setSelectedForEdit(newSelected);
                    return;
                  }
                  setSelectedConv(conv);
                  if (user && conv.unreadCount > 0) {
                    const docRef = doc(db, 'conversations', conv.id);
                    setDoc(docRef, {
                      unreadCounts: {
                        [user.uid]: 0
                      }
                    }, { merge: true }).catch(err => console.error("Error marking as read:", err));
                  }
                }}
                className={`w-full flex items-center p-4 hover:bg-[#1C1C1E] transition-colors border-none ${selectedConv?.id === conv.id && !isEditMode ? 'bg-[#1C1C1E]' : ''}`}
              >
                {isEditMode && (
                  <div className="mr-4">
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${selectedForEdit.has(conv.id) ? 'bg-[#007AFF] border-[#007AFF]' : 'border-white/20'}`}>
                      {selectedForEdit.has(conv.id) && <div className="w-2 h-2 bg-white rounded-full"></div>}
                    </div>
                  </div>
                )}
                <div className="relative">
                  {conv.isGroup ? (
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#111] to-[#222] border border-white/10 flex items-center justify-center shrink-0">
                      <Users className="w-6 h-6 text-[#FF5C00]" />
                    </div>
                  ) : (
                    <img src={conv.displayAvatar} alt={conv.displayName} className="w-12 h-12 rounded-full object-cover shrink-0" />
                  )}
                  {conv.online && !conv.isGroup && <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-[#0A0A0A] rounded-full"></div>}
                </div>
                <div className="ml-3 flex-1 min-w-0 text-left">
                  <div className="flex justify-between items-baseline mb-0.5">
                    <span className="font-bold text-white truncate max-w-[140px] text-[15px]">{conv.displayName}</span>
                    <span className="text-xs text-gray-500 shrink-0">{conv.time}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className={`text-[14px] truncate flex-1 ${conv.unreadCount > 0 ? 'text-white' : 'text-gray-500'}`}>
                      {conv.lastMessage}
                    </p>
                    {conv.unreadCount > 0 && (
                      <div className="ml-2 w-2.5 h-2.5 bg-[#007AFF] rounded-full shrink-0 shadow-[0_0_8px_rgba(0,122,255,0.5)]"></div>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>

          {isEditMode && selectedForEdit.size > 0 && (
            <div className="p-4 border-t border-white/5 bg-black flex justify-between items-center shrink-0">
              <button 
                onClick={async () => {
                  if (user) {
                    try {
                      await Promise.all(Array.from(selectedForEdit).map((id: string) => 
                        setDoc(doc(db, 'conversations', id), {
                          unreadCounts: { [user.uid]: 0 }
                        }, { merge: true })
                      ));
                      setIsEditMode(false);
                      setSelectedForEdit(new Set());
                    } catch (err) {
                      console.error("Error marking as read:", err);
                    }
                  }
                }}
                className="text-[#007AFF] text-[15px] font-medium"
              >
                Tout lire
              </button>
              <button 
                onClick={async () => {
                  if (window.confirm(`Supprimer ${selectedForEdit.size} conversation(s) ?`)) {
                    try {
                      await Promise.all(Array.from(selectedForEdit).map((id: string) => 
                        deleteDoc(doc(db, 'conversations', id))
                      ));
                      setIsEditMode(false);
                      setSelectedForEdit(new Set());
                      setSelectedConv(null);
                    } catch (err) {
                      console.error("Error deleting conversations:", err);
                    }
                  }
                }}
                className="text-[#FF3B30] text-[15px] font-medium"
              >
                Supprimer
              </button>
            </div>
          )}
        </aside>

        {/* Right Area - Chat Area */}
        <section className={`flex-1 flex-col bg-black border-none overflow-hidden h-full ${selectedConv ? 'flex' : 'hidden md:flex'}`}>
          {/* Chat Header */}
          {selectedConv ? (
            <>
          <div className="h-20 px-4 md:px-6 border-b border-white/5 flex items-center justify-between bg-black/80 backdrop-blur-md shrink-0">
            <div className="flex items-center flex-1">
              <button 
                onClick={() => setSelectedConv(null)}
                className="mr-3 p-2 hover:bg-white/5 rounded-full md:hidden text-[#007AFF] transition-colors"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div className="flex flex-col items-center flex-1 md:mr-10">
                <div className="relative">
                  {selectedConv.isGroup ? (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#111] to-[#222] border border-white/10 flex items-center justify-center shrink-0">
                      <Users className="w-5 h-5 text-[#FF5C00]" />
                    </div>
                  ) : (
                    <img src={selectedConv.displayAvatar} alt={selectedConv.displayName} className="w-10 h-10 rounded-full object-cover" />
                  )}
                  {selectedConv.online && !selectedConv.isGroup && <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 border-2 border-black rounded-full"></div>}
                </div>
                <div className="mt-1 flex flex-col items-center">
                  <h2 className="font-bold text-[13px] leading-tight text-white">{selectedConv.displayName}</h2>
                  <div className="text-[10px] text-gray-500 font-medium tracking-wide">
                    {selectedConv.online ? 'En ligne' : 'Vu récemment'}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-1">
              <button className="p-2 text-[#007AFF] hover:bg-white/5 rounded-full transition-colors">
                <Info className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-2 no-scrollbar flex flex-col bg-black">
            <div className="text-center my-6">
              <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                Aujourd'hui {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
            
            {messages.map((msg, idx) => {
              const isFirstInGroup = idx === 0 || messages[idx-1]?.senderId !== msg.senderId;
              const isLastInGroup = idx === messages.length - 1 || messages[idx+1]?.senderId !== msg.senderId;
              
              return (
                <div key={msg.id} className={`flex flex-col ${msg.isMe ? 'items-end' : 'items-start'} ${isLastInGroup ? 'mb-4' : 'mb-0.5'}`}>
                  <div className="flex items-end max-w-[85%] md:max-w-[70%]">
                    {!msg.isMe && isLastInGroup && (
                      <div className="w-8 h-8 mr-2 shrink-0">
                        <img src={msg.avatar || "https://images.unsplash.com/photo-1543807535-eceef0bc6599?auto=format&fit=crop&w=150&q=80"} alt={msg.sender} className="w-8 h-8 rounded-full object-cover" />
                      </div>
                    )}
                    {!msg.isMe && !isLastInGroup && <div className="w-10" />}
                    
                    <div className={`px-4 py-2.5 rounded-2xl text-[15px] ${
                      msg.isMe 
                        ? 'bg-[#007AFF] text-white rounded-tr-lg rounded-br-lg' 
                        : 'bg-[#262626] text-white rounded-tl-lg rounded-bl-lg'
                    } ${isFirstInGroup && msg.isMe ? 'rounded-tr-2xl' : ''} ${isLastInGroup && msg.isMe ? 'rounded-br-2xl' : ''} ${isFirstInGroup && !msg.isMe ? 'rounded-tl-2xl' : ''} ${isLastInGroup && !msg.isMe ? 'rounded-bl-2xl' : ''}`}>
                      {msg.imageUrl ? (
                        <div className="flex flex-col gap-2">
                          <a href={msg.imageUrl} target="_blank" rel="noopener noreferrer">
                            <img src={msg.imageUrl} alt="Message attachment" className="max-w-[200px] sm:max-w-xs md:max-w-sm rounded-xl object-cover cursor-pointer hover:opacity-90 transition-opacity" />
                          </a>
                        </div>
                      ) : (
                        <p className="leading-snug">{msg.text}</p>
                      )}
                    </div>
                  </div>
                  {msg.isMe && isLastInGroup && (
                    <span className="text-[10px] text-gray-500 mt-1 mr-1 font-medium">Distribué</span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Input Area (iOS Style) */}
          <div className="px-4 py-3 bg-black/90 backdrop-blur-xl border-t border-white/5 shrink-0">
            {isBlocked ? (
              <div className="flex flex-col items-center justify-center py-4 px-6 bg-[#ff5a00]/5 border border-[#ff5a00]/20 rounded-3xl text-center">
                <div className="bg-[#ff5a00]/10 p-2 rounded-full mb-2">
                  <CreditCard className="w-5 h-5 text-[#ff5a00]" />
                </div>
                <p className="text-[13px] font-black text-[#ff5a00] uppercase tracking-widest mb-1">Paiement Requis</p>
                <p className="text-[11px] text-gray-400 max-w-xs">
                  Réglez vos frais de stand pour débloquer la messagerie et finaliser les échanges avec l'organisateur.
                </p>
                <button 
                  onClick={() => navigate('/stand/festivals')}
                  className="mt-3 text-[11px] font-black text-white bg-[#ff5a00] px-4 py-2 rounded-xl hover:bg-[#ff6b1a] transition-all uppercase tracking-wider"
                >
                  Procéder au paiement
                </button>
              </div>
            ) : (
              <form onSubmit={handleSendMessage} className="flex items-center gap-3">
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  hidden 
                  accept="image/*" 
                  onChange={handleImageUpload} 
                />
                <button 
                  type="button" 
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className={`w-9 h-9 flex items-center justify-center rounded-full transition-colors shrink-0 ${isUploading ? 'bg-[#333] text-gray-600' : 'bg-[#262626] hover:bg-[#333] text-gray-400 hover:text-white'}`}
                >
                  <Plus className={`w-6 h-6 ${isUploading ? 'animate-spin' : ''}`} />
                </button>
                
                <div className="flex-1 bg-[#1C1C1E] border border-white/10 rounded-full flex items-center px-4 py-1.5 transition-all focus-within:border-white/20">
                  <textarea 
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage(e);
                      }
                    }}
                    placeholder="iMessage"
                    className="flex-1 bg-transparent border-none focus:outline-none text-white text-[15px] resize-none max-h-32 min-h-[36px] py-1.5 scrollbar-hide"
                    rows={1}
                  />
                  
                  <button 
                    type="submit"
                    disabled={!messageInput.trim()}
                    className={`ml-2 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                      messageInput.trim() ? 'bg-[#007AFF] text-white' : 'bg-[#333] text-gray-600'
                    }`}
                  >
                    <ArrowUp className="w-5 h-5 stroke-[3px]" />
                  </button>
                </div>
              </form>
            )}
          </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              Sélectionnez une conversation pour commencer à discuter
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function Badge({ role }: { role: string }) {
  if (role === 'groupe') {
    return <span className="bg-[#4b3cfa]/20 text-[#8c82fc] border border-[#4b3cfa]/30 text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider">Groupe</span>
  }
  if (role === 'festival') {
    return <span className="bg-[#4b3cfa]/20 text-[#8c82fc] border border-[#4b3cfa]/30 text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider">Festival</span>
  }
  return <span className="bg-[#FF5C00]/20 text-[#ff8a33] border border-[#FF5C00]/30 text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider">Stand</span>
}
